import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Loader2, ChevronDown, ChevronUp, AlertCircle, Clock } from 'lucide-react';
import JobCard from './JobCard';
import JobDetailsModal from './JobDetailsModal';
import { jobsApi } from '../../api/jobs';
import { cn } from '../../utils/cn';
import { useLanguage } from '../../contexts/LanguageContext';

const ActiveJobs = () => {
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set([]));
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const jobsData = await jobsApi.getAll();
        
        // Filter out completed and cancelled jobs - only include active ones
        const activeJobs = jobsData.filter(job => job.status !== 'COMPLETED' && job.status !== 'CANCELLED');
        
        // Transform API data to match expected format
        const transformedJobs = await Promise.all(activeJobs.map(async job => {
          // Calculate days until deadline
          const dueDate = new Date(job.application_deadline);
          const today = new Date();
          const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          
          // Determine priority based on days until deadline
          let priority;
          if (daysUntilDue < 0) {
            priority = 'overdue';
          } else if (daysUntilDue <= 3) {
            priority = 'high';
          } else {
            priority = 'low';
          }

          // Get candidates for this job
          const candidates = await jobsApi.getCandidates(job.id);
          
          // Get the actual total references provided (not just the completed ones)
          const totalReferencesProvided = candidates.reduce((total, candidate) => {
            return total + (candidate.references?.length || 0);
          }, 0);
          
          // Calculate references completed (those that filled the form)
          const referencesCompleted = candidates.reduce((total, candidate) => {
            return total + (candidate.references?.filter(ref => ref.filled_form_at)?.length || 0);
          }, 0);

          // Use the actual number of references provided rather than the required amount
          const totalReferencesNeeded = totalReferencesProvided;
          
          return {
            id: job.id,
            title: job.job_title,
            department: job.department,
            location: 'Remote',
            candidates: candidates.length,
            referencesNeeded: totalReferencesNeeded,
            referencesCompleted,
            status: 'active',
            priority,
            dueDate: job.application_deadline,
            daysUntilDue
          };
        }));

        setJobs(transformedJobs);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch jobs:', err);
        setError(t('dashboard.activeJobs.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const toggleSection = (priority: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(priority)) {
      newExpanded.delete(priority);
    } else {
      newExpanded.add(priority);
    }
    setExpandedSections(newExpanded);
  };

  const groupedJobs = jobs.reduce((acc, job) => {
    if (!acc[job.priority]) {
      acc[job.priority] = [];
    }
    acc[job.priority].push(job);
    return acc;
  }, {} as Record<string, typeof jobs>);

  const priorityOrder = ['overdue', 'high', 'low'];

  const getPriorityInfo = (priority: string) => {
    switch (priority) {
      case 'overdue':
        return {
          color: 'bg-red-50 text-red-700 border-red-200',
          hoverBg: 'hover:bg-red-50/50',
          icon: AlertCircle,
          description: t('dashboard.activeJobs.priority.overdueDescription')
        };
      case 'high':
        return {
          color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
          hoverBg: 'hover:bg-yellow-50/50',
          icon: Clock,
          description: t('dashboard.activeJobs.priority.highDescription')
        };
      case 'low':
        return {
          color: 'bg-green-50 text-green-700 border-green-200',
          hoverBg: 'hover:bg-green-50/50',
          icon: Briefcase,
          description: t('dashboard.activeJobs.priority.lowDescription')
        };
      default:
        return {
          color: 'bg-gray-50 text-gray-700 border-gray-200',
          hoverBg: 'hover:bg-gray-50/50',
          icon: Briefcase,
          description: ''
        };
    }
  };

  if (loading) {
    return (
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Briefcase className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-gray-900">{t('dashboard.activeJobs.title')}</h2>
                <div className="w-6 h-6">
                  <Loader2 className="w-full h-full animate-spin text-blue-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500">{t('dashboard.activeJobs.loading')}</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Briefcase className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">{t('dashboard.activeJobs.title')}</h2>
              <p className="text-sm text-red-500">{error}</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <button 
        onClick={() => {
          // Toggle expand/collapse all sections
          const allPriorities = new Set(['overdue', 'high', 'low']);
          const allExpanded = [...allPriorities].filter(p => groupedJobs[p] && groupedJobs[p].length > 0)
            .every(p => expandedSections.has(p));
          setExpandedSections(allExpanded ? new Set() : 
            new Set([...allPriorities].filter(p => groupedJobs[p] && groupedJobs[p].length > 0)));
        }}
        className="flex items-center justify-between w-full text-left hover:bg-blue-50/30 rounded-lg transition-colors px-2 py-1"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
            <Briefcase className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-gray-900">{t('dashboard.activeJobs.title')}</h2>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                {jobs.length}
              </span>
            </div>
            <p className="text-sm text-gray-500">{t('dashboard.activeJobs.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors">
          {[...expandedSections].length === Object.keys(groupedJobs).length ? (
            <>
              <ChevronUp className="w-3 h-3" />
              {t('common.collapseAll')}
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              {t('common.viewAll')}
            </>
          )}
        </div>
      </button>

      {jobs.length > 0 ? (
        <div className="space-y-4">
          {priorityOrder.map(priority => {
            const priorityJobs = groupedJobs[priority] || [];
            if (priorityJobs.length === 0) return null;

            const { color, hoverBg, icon: PriorityIcon, description } = getPriorityInfo(priority);

            return (
              <div key={priority} className="bg-white rounded-xl border shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleSection(priority)}
                  className={cn(
                    "w-full flex items-center justify-between p-4 transition-colors",
                    hoverBg
                  )}
                >
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border",
                      color
                    )}>
                      <PriorityIcon className="w-4 h-4" />
                      <span>{t(`dashboard.activeJobs.priority.${priority}`)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-gray-500">{description}</span>
                      <span className="font-medium">
                        {priorityJobs.length} {priorityJobs.length === 1 ? t('dashboard.activeJobs.job') : t('dashboard.activeJobs.jobs')}
                      </span>
                    </div>
                  </div>
                  {expandedSections.has(priority) ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>

                {expandedSections.has(priority) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-gray-50/50">
                    {priorityJobs
                      .sort((a, b) => a.daysUntilDue - b.daysUntilDue)
                      .map((job) => (
                        <JobCard 
                          key={job.id} 
                          job={job} 
                          onClick={setSelectedJob}
                        />
                      ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 bg-white rounded-lg border">
          <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">{t('dashboard.activeJobs.empty.title')}</h3>
          <p className="text-gray-500 mt-2">{t('dashboard.activeJobs.empty.subtitle')}</p>
        </div>
      )}

      {selectedJob && (
        <JobDetailsModal 
          job={selectedJob} 
          onClose={() => setSelectedJob(null)} 
        />
      )}
    </section>
  );
};

export default ActiveJobs;